import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  useUpdateOrderFulfillmentMutation,
  useEditShopOrderMutation,
} from "../../../features/shop/shopApiSlice";
import {
  ORDER_STATUS_OPTIONS,
  ORDER_STATUSES_EXPORTABLE,
  ORDER_HEADER_SORT_CONFIG,
  ACTIVE_PREPARATION_STATUSES,
  DEFAULT_PDF_FIELDS,
  PREPARATION_SORT_DEFAULT,
  PDF_FIELD_OPTIONS,
  PDF_FIELD_LABELS,
  normalizeSearchText,
  toArrayValue,
  sanitizeFilename,
  getContractName,
  getContractEmail,
  getContractAddress,
  formatMonthFilterLabel,
  getProcessingScopeLabel,
  getStatusMeta,
  buildCompactSummary,
  buildOrderSearchIndex,
  sortOrders,
  aggregateOrderItems,
  sortPreparationItems,
  getPreparationItemKey,
  toCurrency,
} from "./shopUtils";

// ─── PDF generation (kept here to avoid pulling jsPDF into shopUtils) ────────

const createOrdersPdf = ({
  orders,
  selectedFields,
  scopeLabel,
  monthLabel,
  statusLabel,
  searchText,
}) => {
  const allowedFieldKeys = new Set(
    PDF_FIELD_OPTIONS.map((option) => option.key),
  );
  const fieldsToRender = (selectedFields || []).filter((key) =>
    allowedFieldKeys.has(key),
  );
  const fallbackFields = fieldsToRender.length
    ? fieldsToRender
    : DEFAULT_PDF_FIELDS;

  const totalRevenue = orders.reduce(
    (sum, order) => sum + Number(order?.totals?.grandTotal || 0),
    0,
  );

  const rows = orders.map((order) => ({
    date: dayjs(order.createdAt).format("DD.MM.YYYY HH:mm"),
    status: getStatusMeta(order.status).label,
    member: getContractName(order),
    email: getContractEmail(order),
    address: getContractAddress(order),
    items: buildCompactSummary(order.lineItems),
    total: toCurrency(order?.totals?.grandTotal),
    orderId: String(order._id || ""),
  }));

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  doc.setFontSize(16);
  doc.text("Shop Bestelluebersicht", 14, 16);

  doc.setFontSize(10);
  const metadataLines = [
    `Erstellt: ${dayjs().format("DD.MM.YYYY HH:mm")}`,
    `Datenbasis: ${scopeLabel}`,
    `Monat: ${monthLabel}`,
    `Statusfilter: ${statusLabel}`,
    `Suche: ${searchText ? String(searchText).slice(0, 80) : "-"}`,
    `Bestellungen: ${orders.length}`,
    `Gesamtwert: ${toCurrency(totalRevenue)}`,
  ];
  doc.text(metadataLines, 14, 24);

  autoTable(doc, {
    startY: 62,
    columns: fallbackFields.map((fieldKey) => ({
      header: PDF_FIELD_LABELS[fieldKey] || fieldKey,
      dataKey: fieldKey,
    })),
    body: rows,
    styles: { fontSize: 8, cellPadding: 1.8, valign: "top" },
    headStyles: { fillColor: [22, 101, 52], textColor: [255, 255, 255] },
    columnStyles: {
      items: { cellWidth: 70 },
      address: { cellWidth: 54 },
      orderId: { cellWidth: 36 },
    },
    didParseCell: (hookData) => {
      const key = hookData.column?.dataKey;
      if (key === "items" || key === "address") {
        hookData.cell.styles.overflow = "linebreak";
      }
    },
  });

  const scopeSuffix = sanitizeFilename(scopeLabel) || "datenbasis";
  doc.save(
    `shop-bestellungen-${scopeSuffix}-${dayjs().format("YYYYMMDD_HHmm")}.pdf`,
  );
};

const createPreparationPdf = ({
  items,
  orderCount,
  totalQuantity,
  totalRevenue,
  scopeLabel,
  onlyOpenOrders,
}) => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  doc.setFontSize(16);
  doc.text("Shop Kommissionierliste", 14, 16);

  doc.setFontSize(10);
  const metadataLines = [
    `Erstellt: ${dayjs().format("DD.MM.YYYY HH:mm")}`,
    `Datenbasis: ${scopeLabel}`,
    `Statusbasis: ${
      onlyOpenOrders
        ? "Nur Neu/In Bearbeitung/Freigegeben"
        : "Alle enthaltenen Status"
    }`,
    `Bestellungen in Liste: ${orderCount}`,
    `Produktvarianten: ${items.length}`,
    `Menge gesamt: ${totalQuantity}`,
    `Umsatz gesamt: ${toCurrency(totalRevenue)}`,
  ];
  doc.text(metadataLines, 14, 24);

  autoTable(doc, {
    startY: 62,
    columns: [
      { header: "Produkt", dataKey: "productName" },
      { header: "Variante", dataKey: "variantLabel" },
      { header: "Menge", dataKey: "quantity" },
      { header: "Bestellungen", dataKey: "orderCount" },
      { header: "Umsatz", dataKey: "revenue" },
    ],
    body: items.map((item) => ({
      productName: item.productName,
      variantLabel: item.variantLabel,
      quantity: item.quantity,
      orderCount: item.orderCount,
      revenue: toCurrency(item.revenue),
    })),
    styles: { fontSize: 8.5, cellPadding: 1.8 },
    headStyles: { fillColor: [2, 132, 199], textColor: [255, 255, 255] },
  });

  const scopeSuffix = sanitizeFilename(scopeLabel) || "datenbasis";
  doc.save(
    `shop-kommissionierliste-${scopeSuffix}-${dayjs().format(
      "YYYYMMDD_HHmm",
    )}.pdf`,
  );
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export default function useShopOrdersViewState({
  adminOrders,
  adminMonth,
  orderStatusFilter,
  selectedOrderIds,
  setSelectedOrderIds,
  onCancelOrders,
  onDeleteOrders,
  onBulkStatusChange,
  onOrderStatusChange,
  runningAdminAction,
  products,
}) {
  const [updateOrderFulfillment] = useUpdateOrderFulfillmentMutation();
  const [editShopOrder, { isLoading: editingOrder, error: editOrderError }] =
    useEditShopOrderMutation();

  // ── Local view state ──
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("dateDesc");
  const [fulfillmentStatusFilter, setFulfillmentStatusFilter] = useState([]);
  const [optionalTableColumns, setOptionalTableColumns] = useState(["email"]);
  const [processingScope, setProcessingScope] = useState("visible");
  const [pdfFields, setPdfFields] = useState(DEFAULT_PDF_FIELDS);
  const [onlyOpenForPreparation, setOnlyOpenForPreparation] = useState(true);
  const [preparationSort, setPreparationSort] = useState(
    PREPARATION_SORT_DEFAULT,
  );
  const [selectedPreparationItemKeys, setSelectedPreparationItemKeys] =
    useState([]);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    confirmLabel: "",
    confirmColor: "error",
    onConfirm: () => {},
  });
  const [editDialogOrder, setEditDialogOrder] = useState(null);

  // ── Derived state ──
  const normalizedStatusFilter = useMemo(() => {
    const validStatuses = new Set(ORDER_STATUS_OPTIONS);
    const unique = new Set();
    toArrayValue(orderStatusFilter).forEach((status) => {
      const normalized = String(status || "").trim();
      if (!normalized || !validStatuses.has(normalized)) return;
      unique.add(normalized);
    });
    return Array.from(unique);
  }, [orderStatusFilter]);

  const statusFilterLabel = useMemo(() => {
    if (!normalizedStatusFilter.length) return "Keine";
    if (normalizedStatusFilter.length === ORDER_STATUS_OPTIONS.length)
      return "Alle";
    return normalizedStatusFilter
      .map((status) => getStatusMeta(status).label)
      .join(", ");
  }, [normalizedStatusFilter]);

  const normalizedSearchTerm = normalizeSearchText(searchTerm);

  const indexedOrders = useMemo(
    () =>
      adminOrders.map((order) => ({
        order,
        searchIndex: buildOrderSearchIndex(order),
      })),
    [adminOrders],
  );

  const visibleOrders = useMemo(() => {
    let filtered = normalizedSearchTerm
      ? indexedOrders
          .filter((entry) => entry.searchIndex.includes(normalizedSearchTerm))
          .map((entry) => entry.order)
      : adminOrders;

    if (fulfillmentStatusFilter.length > 0) {
      const filterSet = new Set(fulfillmentStatusFilter);
      filtered = filtered.filter((order) =>
        filterSet.has(order.fulfillment?.status || "new"),
      );
    }

    return sortOrders(filtered, sortBy);
  }, [
    adminOrders,
    indexedOrders,
    normalizedSearchTerm,
    sortBy,
    fulfillmentStatusFilter,
  ]);

  const selectedOrders = useMemo(
    () => adminOrders.filter((order) => selectedOrderIds.includes(order._id)),
    [adminOrders, selectedOrderIds],
  );

  const visibleOrderIds = useMemo(
    () => visibleOrders.map((order) => order._id),
    [visibleOrders],
  );

  const visibleOrderIdSet = useMemo(
    () => new Set(visibleOrderIds),
    [visibleOrderIds],
  );

  const visibleSelectedCount = useMemo(
    () =>
      selectedOrderIds.reduce(
        (count, orderId) => count + (visibleOrderIdSet.has(orderId) ? 1 : 0),
        0,
      ),
    [selectedOrderIds, visibleOrderIdSet],
  );

  const allOrdersSelected =
    visibleOrders.length > 0 && visibleSelectedCount === visibleOrders.length;
  const partiallySelected =
    visibleSelectedCount > 0 && visibleSelectedCount < visibleOrders.length;

  const exportableSelectedOrderIds = useMemo(
    () =>
      selectedOrders
        .filter((order) => ORDER_STATUSES_EXPORTABLE.has(order.status))
        .map((order) => order._id),
    [selectedOrders],
  );

  const cancelableSelectedOrderIds = useMemo(
    () =>
      selectedOrders
        .filter((order) => order.status !== "canceled")
        .map((order) => order._id),
    [selectedOrders],
  );

  const selectedOrdersTotal = useMemo(
    () =>
      selectedOrders.reduce(
        (sum, order) => sum + Number(order?.totals?.grandTotal || 0),
        0,
      ),
    [selectedOrders],
  );

  const processingOrders = useMemo(() => {
    if (processingScope === "selected") return selectedOrders;
    if (processingScope === "all") return adminOrders;
    return visibleOrders;
  }, [processingScope, selectedOrders, adminOrders, visibleOrders]);

  const processingScopeLabel = getProcessingScopeLabel(processingScope);

  const processingOrdersTotal = useMemo(
    () =>
      processingOrders.reduce(
        (sum, order) => sum + Number(order?.totals?.grandTotal || 0),
        0,
      ),
    [processingOrders],
  );

  const preparationOrders = useMemo(
    () =>
      onlyOpenForPreparation
        ? processingOrders.filter((order) =>
            ACTIVE_PREPARATION_STATUSES.has(order.status),
          )
        : processingOrders,
    [onlyOpenForPreparation, processingOrders],
  );

  const preparationSummary = useMemo(
    () => aggregateOrderItems(preparationOrders),
    [preparationOrders],
  );

  const sortedPreparationItems = useMemo(
    () => sortPreparationItems(preparationSummary.items, preparationSort),
    [preparationSummary.items, preparationSort],
  );

  const preparationSortField = preparationSort.field;
  const preparationSortDirection = preparationSort.direction;

  const selectedPreparationKeySet = useMemo(
    () => new Set(selectedPreparationItemKeys),
    [selectedPreparationItemKeys],
  );

  const visiblePreparationItemKeys = useMemo(
    () => sortedPreparationItems.map((item) => getPreparationItemKey(item)),
    [sortedPreparationItems],
  );

  const visiblePreparationKeySet = useMemo(
    () => new Set(visiblePreparationItemKeys),
    [visiblePreparationItemKeys],
  );

  const visibleSelectedPreparationCount = useMemo(
    () =>
      selectedPreparationItemKeys.reduce(
        (count, key) => count + (visiblePreparationKeySet.has(key) ? 1 : 0),
        0,
      ),
    [selectedPreparationItemKeys, visiblePreparationKeySet],
  );

  const allPreparationItemsSelected =
    sortedPreparationItems.length > 0 &&
    visibleSelectedPreparationCount === sortedPreparationItems.length;
  const partiallySelectedPreparationItems =
    visibleSelectedPreparationCount > 0 &&
    visibleSelectedPreparationCount < sortedPreparationItems.length;

  useEffect(() => {
    setSelectedPreparationItemKeys((prev) =>
      prev.filter((key) => visiblePreparationKeySet.has(key)),
    );
  }, [visiblePreparationKeySet]);

  const showEmailColumn = optionalTableColumns.includes("email");
  const showAddressColumn = optionalTableColumns.includes("address");
  const showOrderIdColumn = optionalTableColumns.includes("orderId");
  const desktopTableColSpan = 6 + optionalTableColumns.length;
  const hasSearchNoResults =
    Boolean(normalizedSearchTerm) &&
    adminOrders.length > 0 &&
    visibleOrders.length === 0;

  // ── Handlers ──
  const closeConfirm = () =>
    setConfirmDialog((prev) => ({ ...prev, open: false }));

  const toggleExpand = (orderId) =>
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));

  const handleToggleOrderSelection = (orderId, checked) => {
    setSelectedOrderIds((prev) => {
      if (checked) return Array.from(new Set([...prev, orderId]));
      return prev.filter((id) => id !== orderId);
    });
  };

  const handleToggleSelectAll = (checked) => {
    const visibleIds = visibleOrders.map((order) => order._id);
    const visibleIdsSet = new Set(visibleIds);
    if (checked) {
      setSelectedOrderIds((prev) =>
        Array.from(new Set([...prev, ...visibleIds])),
      );
      return;
    }
    setSelectedOrderIds((prev) => prev.filter((id) => !visibleIdsSet.has(id)));
  };

  const handleOrderHeaderSort = (field) => {
    const config = ORDER_HEADER_SORT_CONFIG[field];
    if (!config) return;
    setSortBy((prev) => {
      if (prev === config.asc) return config.desc;
      if (prev === config.desc) return config.asc;
      return config[config.defaultDirection];
    });
  };

  const getOrderSortMeta = (field) => {
    const config = ORDER_HEADER_SORT_CONFIG[field];
    if (!config) return { active: false, direction: "asc" };
    if (sortBy === config.asc) return { active: true, direction: "asc" };
    if (sortBy === config.desc) return { active: true, direction: "desc" };
    return { active: false, direction: config.defaultDirection };
  };

  const handlePreparationSort = (field) => {
    const defaultDirection =
      field === "quantity" || field === "orderCount" || field === "revenue"
        ? "desc"
        : "asc";
    setPreparationSort((prev) => {
      if (prev.field === field) {
        return { field, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { field, direction: defaultDirection };
    });
  };

  const handleTogglePreparationSelection = (itemKey, checked) => {
    setSelectedPreparationItemKeys((prev) => {
      if (checked) return Array.from(new Set([...prev, itemKey]));
      return prev.filter((key) => key !== itemKey);
    });
  };

  const handleTogglePreparationSelectAll = (checked) => {
    if (checked) {
      setSelectedPreparationItemKeys((prev) =>
        Array.from(new Set([...prev, ...visiblePreparationItemKeys])),
      );
      return;
    }
    setSelectedPreparationItemKeys((prev) =>
      prev.filter((key) => !visiblePreparationKeySet.has(key)),
    );
  };

  const handleCancelSingleOrder = (order) => {
    if (!order?._id || order.status === "canceled") return;
    setConfirmDialog({
      open: true,
      title: "Bestellung stornieren",
      message: "Soll diese Bestellung wirklich storniert werden?",
      confirmLabel: "Stornieren",
      confirmColor: "warning",
      onConfirm: () => {
        closeConfirm();
        onCancelOrders([order._id], `Bestellung ${order._id} wurde storniert.`);
      },
    });
  };

  const handleDeleteSingleOrder = (order) => {
    if (!order?._id) return;
    setConfirmDialog({
      open: true,
      title: "Bestellung löschen",
      message:
        "Soll diese Bestellung wirklich gelöscht werden? Dieser Schritt ist dauerhaft.",
      confirmLabel: "Endgültig löschen",
      confirmColor: "error",
      onConfirm: () => {
        closeConfirm();
        onDeleteOrders([order._id], `Bestellung ${order._id} wurde gelöscht.`);
      },
    });
  };

  const handleEditOrder = (order) => {
    if (!order?._id) return;
    setEditDialogOrder(order);
  };

  const handleSaveEditOrder = async (payload) => {
    try {
      await editShopOrder(payload).unwrap();
      setEditDialogOrder(null);
    } catch {
      // error displayed via RTK Query — dialog stays open
    }
  };

  const handleCancelSelected = () => {
    if (!cancelableSelectedOrderIds.length) return;
    setConfirmDialog({
      open: true,
      title: "Ausgewählte stornieren",
      message: `${cancelableSelectedOrderIds.length} ausgewählte Bestellung(en) stornieren?`,
      confirmLabel: "Stornieren",
      confirmColor: "warning",
      onConfirm: () => {
        closeConfirm();
        onCancelOrders(
          cancelableSelectedOrderIds,
          `${cancelableSelectedOrderIds.length} Bestellung(en) wurden storniert.`,
        );
      },
    });
  };

  const handleDeleteSelected = () => {
    if (!selectedOrderIds.length) return;
    setConfirmDialog({
      open: true,
      title: "Ausgewählte löschen",
      message: `${selectedOrderIds.length} ausgewählte Bestellung(en) löschen? Dieser Schritt ist dauerhaft.`,
      confirmLabel: "Endgültig löschen",
      confirmColor: "error",
      onConfirm: () => {
        closeConfirm();
        onDeleteOrders(
          selectedOrderIds,
          `${selectedOrderIds.length} Bestellung(en) wurden gelöscht.`,
        );
      },
    });
  };

  const handleBulkStatusChange = (status) => {
    if (!selectedOrderIds.length) return;
    const meta = getStatusMeta(status);
    setConfirmDialog({
      open: true,
      title: "Status ändern",
      message: `${selectedOrderIds.length} Bestellung(en) auf "${meta.label}" setzen?`,
      confirmLabel: "Übernehmen",
      confirmColor: "primary",
      onConfirm: () => {
        closeConfirm();
        onBulkStatusChange(
          selectedOrderIds,
          status,
          `${selectedOrderIds.length} Bestellung(en) wurden auf "${meta.label}" gesetzt.`,
        );
      },
    });
  };

  const handleBulkActionSubmit = (bulkAction) => {
    if (!bulkAction || !selectedOrderIds.length) return;
    if (bulkAction === "delete") {
      handleDeleteSelected();
    } else if (bulkAction === "status:canceled") {
      handleCancelSelected();
    } else if (bulkAction.startsWith("status:")) {
      handleBulkStatusChange(bulkAction.replace("status:", ""));
    }
  };

  const handlePrintOrdersPdf = () => {
    if (!processingOrders.length) return;
    createOrdersPdf({
      orders: processingOrders,
      selectedFields: pdfFields,
      scopeLabel: processingScopeLabel,
      monthLabel: formatMonthFilterLabel(adminMonth),
      statusLabel: statusFilterLabel,
      searchText: searchTerm,
    });
  };

  const handlePrintPreparationPdf = () => {
    if (!preparationSummary.items.length) return;
    createPreparationPdf({
      items: sortedPreparationItems,
      orderCount: preparationOrders.length,
      totalQuantity: preparationSummary.totalQuantity,
      totalRevenue: preparationSummary.totalRevenue,
      scopeLabel: processingScopeLabel,
      onlyOpenOrders: onlyOpenForPreparation,
    });
  };

  return {
    // View state + setters
    expandedOrderId,
    searchTerm,
    setSearchTerm,
    sortBy,
    setSortBy,
    fulfillmentStatusFilter,
    setFulfillmentStatusFilter,
    optionalTableColumns,
    setOptionalTableColumns,
    processingScope,
    setProcessingScope,
    pdfFields,
    setPdfFields,
    onlyOpenForPreparation,
    setOnlyOpenForPreparation,
    confirmDialog,
    editDialogOrder,
    setEditDialogOrder,
    editingOrder,
    editOrderError,

    // Derived data
    normalizedStatusFilter,
    statusFilterLabel,
    visibleOrders,
    selectedOrders,
    visibleSelectedCount,
    allOrdersSelected,
    partiallySelected,
    exportableSelectedOrderIds,
    cancelableSelectedOrderIds,
    selectedOrdersTotal,
    processingOrders,
    processingScopeLabel,
    processingOrdersTotal,
    preparationOrders,
    preparationSummary,
    sortedPreparationItems,
    preparationSortField,
    preparationSortDirection,
    selectedPreparationKeySet,
    visibleSelectedPreparationCount,
    allPreparationItemsSelected,
    partiallySelectedPreparationItems,
    showEmailColumn,
    showAddressColumn,
    showOrderIdColumn,
    desktopTableColSpan,
    hasSearchNoResults,

    // Handlers
    closeConfirm,
    toggleExpand,
    handleToggleOrderSelection,
    handleToggleSelectAll,
    handleOrderHeaderSort,
    getOrderSortMeta,
    handlePreparationSort,
    handleTogglePreparationSelection,
    handleTogglePreparationSelectAll,
    handleCancelSingleOrder,
    handleDeleteSingleOrder,
    handleEditOrder,
    handleSaveEditOrder,
    handleCancelSelected,
    handleDeleteSelected,
    handleBulkActionSubmit,
    handlePrintOrdersPdf,
    handlePrintPreparationPdf,
    updateOrderFulfillment,
    onOrderStatusChange,
  };
}
